'use client';

import React from 'react';
import Link from 'next/link';
import { Booking, getServicesByIds, formatPrice, formatDuration } from '@/lib/bookingStore';

interface Props {
  booking: Booking;
}

export default function AgendarConfirmation({ booking }: Props) {
  const services = getServicesByIds(booking.serviceIds);

  const formatDateDisplay = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    const days = ['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'];
    const date = new Date(y, m - 1, d);
    return `${days[date.getDay()]}, ${d} de ${months[m - 1]} de ${y}`;
  };

  const whatsappMessage = encodeURIComponent(
    `Olá, MS Beauty & Care!\n\nA minha marcação foi confirmada.\n\nNome: ${booking.customerName}\n\nServiços:\n${services.map(s => `• ${s.name}`).join('\n')}\n\nData: ${formatDateDisplay(booking.date)}\nHora: ${booking.startTime}\nDuração: ${formatDuration(booking.totalDuration)}\n\nTotal: ${formatPrice(booking.totalPrice)}\n\nObrigada!`
  );
  const whatsappUrl = `https://wa.me/258874639388?text=${whatsappMessage}`;

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `SUMMARY:MS Beauty & Care — ${services.map(s => s.name).join(', ')}`,
    `DTSTART:${booking.date.replace(/-/g, '')}T${booking.startTime.replace(':', '')}00`,
    `DTEND:${booking.date.replace(/-/g, '')}T${booking.endTime.replace(':', '')}00`,
    'LOCATION:Rua da Malhangalene, Maputo, Moçambique',
    `DESCRIPTION:Sessão na MS Beauty & Care\\nTotal: ${formatPrice(booking.totalPrice)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\n');

  const downloadIcs = () => {
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ms-beauty-care-marcacao.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-center">
          <div className="text-center">
            <p className="font-serif text-base font-light tracking-[0.1em] text-dark-brown">MS BEAUTY</p>
            <p className="font-sans text-[8px] tracking-[0.3em] text-burnt-rose font-medium uppercase">& CARE</p>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-2xl mx-auto w-full px-6 py-12">
        {/* Success icon */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-berry-gradient flex items-center justify-center mx-auto mb-6 shadow-lg">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <span className="gold-line mx-auto mb-4 block" />
          <h1 className="font-serif text-4xl lg:text-5xl font-light text-dark-brown mb-3">
            Marcação<br /><em className="italic text-burnt-rose">Confirmada!</em>
          </h1>
          <p className="text-sm text-foreground/60 max-w-sm mx-auto">
            A sua sessão foi reservada com sucesso. Até breve na MS Beauty & Care!
          </p>
        </div>

        {/* Booking details card */}
        <div className="bg-white border border-border rounded-2xl overflow-hidden mb-8">
          <div className="p-6 border-b border-border">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-3">Serviços</p>
            <div className="space-y-1.5">
              {services.map(s => (
                <div key={s.id} className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{s.name}</span>
                  <span className="text-sm font-semibold tabular-nums text-foreground">{formatPrice(s.price)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 border-b border-border grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-1">Data</p>
              <p className="text-sm font-medium text-foreground">{formatDateDisplay(booking.date)}</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-1">Horário</p>
              <p className="text-sm font-medium text-foreground">{booking.startTime}</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-1">Duração</p>
              <p className="text-sm font-medium text-foreground">{formatDuration(booking.totalDuration)}</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-1">Cliente</p>
              <p className="text-sm font-medium text-foreground">{booking.customerName}</p>
            </div>
          </div>

          <div className="p-6 bg-ivory flex items-center justify-between">
            <span className="font-semibold text-foreground">Total</span>
            <span className="font-bold text-2xl text-burnt-rose tabular-nums">{formatPrice(booking.totalPrice)}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] text-white rounded-full font-semibold text-sm tracking-[0.1em] uppercase hover:bg-[#1ebe5d] transition-colors shadow-md"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Confirmar pelo WhatsApp
          </a>

          <button
            onClick={downloadIcs}
            className="flex items-center justify-center gap-2 w-full py-4 btn-outline rounded-full font-semibold text-sm tracking-[0.1em] uppercase"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Adicionar ao Calendário
          </button>

          <Link
            href="/"
            className="flex items-center justify-center w-full py-4 text-sm text-foreground/50 hover:text-burnt-rose transition-colors"
          >
            Voltar ao site
          </Link>
        </div>
      </div>
    </div>
  );
}
